/**
 * Pricing.jsx - Pricing Section Component
 * 
 * @description
 * Displays the three-tier pricing structure for QuickLinks.
 * Used on the homepage to showcase subscription options.
 * 
 * @location Homepage section
 * @scrollTarget #pricing
 * 
 * @tiers
 * 1. Free ($0/month)
 *    - Unlimited links
 *    - 2 pre-built themes
 *    - 5-day basic analytics
 *    - Email support
 *    - "Hosted by QuickLinks" footer
 * 
 * 2. Pro ($2/month) - Most Popular
 *    - Everything in Free
 *    - 5 themes + color customization
 *    - 30-day detailed analytics
 *    - SEO customization
 *    - Custom footer text
 *    - Priority email support (24-48h)
 * 
 * 3. Max ($4/month)
 *    - Everything in Pro
 *    - Full CSS customization
 *    - Lifetime analytics with exports
 *    - Remove all branding
 *    - Automated SEO reports
 *    - 24/7 chat support
 * 
 * @features
 * - Animated cards on scroll
 * - "Most Popular" badge on Pro tier
 * - Responsive grid layout
 * - Call-to-action buttons
 * - Feature comparison option
 * 
 * @tierStructure
 * {
 *   name: string - Tier name
 *   price: string - Monthly price (number as string)
 *   description: string - Short description
 *   features: string[] - List of features
 *   cta: string - Button text
 *   ctaLink: string - Button destination
 *   popular: boolean - Show popular badge
 * }
 * 
 * @styling
 * - Gray background section
 * - White cards with shadows
 * - Purple accent for popular tier
 * - Gradient buttons
 * - Responsive breakpoints
 * 
 * @animations
 * - Fade in on scroll
 * - Staggered card animations
 * - Scale effect on popular tier
 * 
 * @dependencies
 * - React Router for navigation links
 * - Tailwind for styling
 */

import { Link } from 'react-router-dom';

const Pricing = () => {
  const tiers = [
    {
      name: 'Free',
      price: '0',
      description: 'Perfect for getting started',
      features: [
        'Unlimited links',
        '2 pre-built themes',
        '5-day basic analytics',
        'Email support',
        '"Hosted by Link Spaghetti ❤️" footer',
      ],
      cta: 'Start Free',
      ctaLink: '/signup',
      popular: false,
    },
    {
      name: 'Pro',
      price: '2',
      description: 'For creators who want more control',
      features: [
        'Everything in Free',
        '5 themes + color customization',
        '30-day detailed analytics',
        'SEO customization',
        'Custom footer text',
        'Priority email support (24-48h)',
      ],
      cta: 'Go Pro',
      ctaLink: '/signup',
      popular: true,
    },
    {
      name: 'Max',
      price: '4',
      description: 'Maximum features & flexibility',
      features: [
        'Everything in Pro',
        'Full CSS customization',
        'Lifetime analytics with exports',
        'Remove all branding',
        'Automated SEO reports',
        '24/7 chat support',
      ],
      cta: 'Go Max',
      ctaLink: '/signup',
      popular: false,
    },
  ];

  return (
    <section id="pricing" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16 animate-fade-in">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            Simple, transparent pricing
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Choose the perfect plan for your needs. Always flexible to scale up or down.
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
          {tiers.map((tier, index) => (
            <div
              key={tier.name}
              className={`relative bg-white rounded-2xl shadow-lg p-8 animate-fade-in-up ${
                tier.popular ? 'ring-2 ring-purple-600 scale-105' : ''
              }`}
              style={{ animationDelay: `${index * 100}ms` }}
            >
              {/* Popular Badge */}
              {tier.popular && (
                <div className="absolute -top-5 left-1/2 transform -translate-x-1/2">
                  <span className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-1 rounded-full text-sm font-medium">
                    Most Popular
                  </span>
                </div>
              )}

              {/* Tier Name */}
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{tier.name}</h3>
                <p className="text-gray-600 mb-4">{tier.description}</p>
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-5xl font-bold text-gray-900">${tier.price}</span>
                  <span className="text-gray-600">/month</span>
                </div>
              </div>

              {/* Features */}
              <ul className="space-y-4 mb-8">
                {tier.features.map((feature, featureIndex) => (
                  <li key={featureIndex} className="flex items-start gap-3">
                    <svg
                      className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                        tier.popular ? 'text-purple-600' : 'text-green-500'
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    <span className="text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>

              {/* CTA Button */}
              <Link
                to={tier.ctaLink}
                className={`block w-full text-center py-3 px-6 rounded-lg font-medium transition-all duration-200 transform hover:scale-105 ${
                  tier.popular
                    ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700'
                    : 'bg-gray-900 text-white hover:bg-gray-800'
                }`}
              >
                {tier.cta}
              </Link>
            </div>
          ))}
        </div>

        {/* Bottom Section - Removed text about SSL, uptime, and sales team */}
        <div className="mt-16 text-center animate-fade-in" style={{ animationDelay: '400ms' }}>
          {/* Removed content as requested */}
        </div>
      </div>
    </section>
  );
};

export default Pricing; 